import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sparkles, RefreshCw, BookmarkPlus } from "lucide-react";
import FeedbackFooter from "@/components/FeedbackFooter";

export default function Affirmations() {
    const [daily, setDaily] = useState(null);
    const [random, setRandom] = useState(null);
    const [all, setAll] = useState([]);

    useEffect(() => {
        api.get("/affirmations/daily").then(({ data }) => setDaily(data));
        api.get("/affirmations/random").then(({ data }) => setRandom(data));
        api.get("/affirmations").then(({ data }) => setAll(data.affirmations || []));
    }, []);

    const rerollRandom = async () => {
        const { data } = await api.get("/affirmations/random");
        setRandom(data);
    };

    const saveOne = async (text) => {
        try {
            await api.post("/favorites", { text, source: "affirmation" });
            toast.success("Saved to favorites");
        } catch {
            toast.error("Could not save");
        }
    };

    return (
        <div className="max-w-5xl mx-auto p-6 sm:p-10 lg:p-14">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mb-10">
                <p className="text-sm uppercase tracking-widest text-muted-foreground mb-3 font-medium">Affirmations</p>
                <h1 className="font-heading text-4xl sm:text-5xl font-[800] tracking-tight">
                    Small kind words <span className="font-[300] italic">for right now.</span>
                </h1>
            </motion.div>

            {/* Daily */}
            <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.1 }}
                className="relative overflow-hidden bg-gradient-to-br from-primary/20 to-accent rounded-3xl p-8 sm:p-12 border border-border shadow-soft-lg mb-8"
            >
                <Sparkles className="w-8 h-8 text-primary mb-6" strokeWidth={1.5} />
                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-4 font-medium">Today's affirmation</p>
                <p className="font-heading text-3xl sm:text-4xl font-[300] italic leading-snug mb-8" data-testid="daily-affirmation">
                    “{daily?.text || "Loading…"}”
                </p>
                {daily?.text && (
                    <Button variant="outline" size="sm" onClick={() => saveOne(daily.text)} className="rounded-full bg-white/70 backdrop-blur-xl" data-testid="save-daily-btn">
                        <BookmarkPlus className="w-4 h-4 mr-2" strokeWidth={1.5} /> Save
                    </Button>
                )}
            </motion.div>

            {/* Random */}
            <div className="bg-card rounded-3xl p-8 border border-border shadow-soft mb-8">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-primary" strokeWidth={1.5} />
                        <h2 className="font-heading text-lg font-[700] tracking-tight">Need another?</h2>
                    </div>
                    <Button variant="ghost" size="sm" onClick={rerollRandom} className="rounded-full" data-testid="reroll-btn">
                        <RefreshCw className="w-4 h-4 mr-2" strokeWidth={1.5} /> Give me another
                    </Button>
                </div>
                <motion.p
                    key={random?.text}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="font-heading text-2xl font-[400] italic leading-snug mb-6"
                    data-testid="random-affirmation"
                >
                    “{random?.text || "…"}”
                </motion.p>
                {random?.text && (
                    <Button variant="outline" size="sm" onClick={() => saveOne(random.text)} className="rounded-full bg-background">
                        <BookmarkPlus className="w-4 h-4 mr-2" strokeWidth={1.5} /> Save
                    </Button>
                )}
            </div>

            {/* All */}
            <h2 className="font-heading text-xl font-[700] mb-6 tracking-tight">All affirmations</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {all.map((text, i) => (
                    <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: (i % 6) * 0.05 }}
                        className="bg-card rounded-2xl p-6 border border-border shadow-soft flex items-start justify-between gap-4 group hover:-translate-y-1 transition-transform duration-300"
                    >
                        <p className="leading-relaxed">{text}</p>
                        <button onClick={() => saveOne(text)} className="opacity-0 group-hover:opacity-100 p-2 rounded-full hover:bg-accent text-muted-foreground hover:text-primary transition-opacity duration-200" data-testid={`save-affirmation-${i}`}>
                            <BookmarkPlus className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                    </motion.div>
                ))}
            </div>
            <FeedbackFooter />
        </div>
    );
}
